import React, { useState } from 'react';
import {
  X,
  Edit3,
  Folder,
  Tag,
  Trash2,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { PREDEFINED_CATEGORIES } from './DocumentUploadModal';

export function RenameDocumentModal({
  open,
  document,
  onClose,
  onRename,
}) {
  const [newName, setNewName] = useState(document?.title || '');

  if (!open || !document) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    onRename(document, newName.trim());
    onClose();
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer doc-drawer ios-doc-drawer doc-action-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Rename Document"
      >
        <div className="card-drawer-header modal-header">
          <div className="modal-header-info">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4' }}
            >
              <Edit3 size={18} />
            </div>
            <div className="modal-header-titles">
              <h3 className="modal-title">Rename Document</h3>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="identity-editor-form card-drawer-form-wrapper">
          <div className="card-drawer-body modal-body">
            <div className="form-group">
              <label className="form-label">Document Name</label>
              <input
                type="text"
                className="form-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                required
              />
            </div>
          </div>

          <div className="card-drawer-footer modal-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={!newName.trim()}>
              Save Name
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

export function MoveDocumentCategoryModal({
  open,
  document,
  categories = PREDEFINED_CATEGORIES,
  onClose,
  onMoveCategory,
}) {
  const [selectedCategory, setSelectedCategory] = useState(document?.category || 'Personal');

  if (!open || !document) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onMoveCategory(document, selectedCategory);
    onClose();
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer doc-drawer ios-doc-drawer doc-action-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Move Document Category"
      >
        <div className="card-drawer-header modal-header">
          <div className="modal-header-info">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}
            >
              <Folder size={18} />
            </div>
            <div className="modal-header-titles">
              <h3 className="modal-title">Move Category</h3>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="identity-editor-form card-drawer-form-wrapper">
          <div className="card-drawer-body modal-body">
            <p className="text-secondary text-sm" style={{ marginBottom: '14px' }}>
              Select a target category for "<strong>{document.title}</strong>".
            </p>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-input"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map((c) => (
                  <option key={typeof c === 'string' ? c : c.name} value={typeof c === 'string' ? c : c.name}>
                    {typeof c === 'string' ? c : c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card-drawer-footer modal-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              Move Category
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

export function ManageDocumentTagsModal({
  open,
  document,
  onClose,
  onSaveTags,
}) {
  const [tags, setTags] = useState(document?.tags || []);
  const [tagInput, setTagInput] = useState('');

  if (!open || !document) return null;

  const handleAdd = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, '').toLowerCase();
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleRemove = (tToRemove) => {
    setTags(tags.filter((t) => t !== tToRemove));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSaveTags(document, tags);
    onClose();
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer doc-drawer ios-doc-drawer doc-action-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Manage Document Tags"
      >
        <div className="card-drawer-header modal-header">
          <div className="modal-header-info">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}
            >
              <Tag size={18} />
            </div>
            <div className="modal-header-titles">
              <h3 className="modal-title">Manage Tags</h3>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="identity-editor-form card-drawer-form-wrapper">
          <div className="card-drawer-body modal-body">
            <div className="form-group">
              <label className="form-label">Tags (Type and press Enter)</label>
              <div className="tags-input-wrapper">
                {tags.map((t) => (
                  <span key={t} className="tag-chip">
                    <span>#{t}</span>
                    <button type="button" className="tag-remove-btn" onClick={() => handleRemove(t)}>
                      &times;
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="tags-bare-input"
                  placeholder="Add a tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAdd}
                />
              </div>
            </div>
          </div>

          <div className="card-drawer-footer modal-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              Save Tags
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

export function DeleteDocumentModal({
  open,
  document,
  isPermanent = false,
  onClose,
  onConfirm,
}) {
  const [typedConfirm, setTypedConfirm] = useState('');

  if (!open || !document) return null;

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer doc-drawer ios-doc-drawer doc-action-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label={isPermanent ? 'Delete Document Permanently' : 'Move Document to Trash'}
      >
        <div className="card-drawer-header modal-header">
          <div className="modal-header-info">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}
            >
              <AlertTriangle size={18} />
            </div>
            <div className="modal-header-titles">
              <h3 className="modal-title">
                {isPermanent ? 'Delete Permanently?' : 'Move to Trash?'}
              </h3>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="card-drawer-body modal-body">
          <p className="text-secondary" style={{ lineHeight: 1.5, fontSize: '0.88rem' }}>
            {isPermanent ? (
              <>
                Are you sure you want to permanently erase "<strong>{document.title}</strong>"?
                This action is irreversible and the zero-knowledge encrypted payload cannot be recovered.
              </>
            ) : (
              <>
                Move "<strong>{document.title}</strong>" to Trash? You can restore it anytime
                from the Trash sidebar navigation.
              </>
            )}
          </p>
        </div>

        <div className="card-drawer-footer modal-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              onConfirm(document, isPermanent);
              onClose();
            }}
          >
            <Trash2 size={14} />
            <span>{isPermanent ? 'Erase Forever' : 'Move to Trash'}</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
